/**
 * Sales Return Helper
 * 
 * Contains types, mock data, status colors, and utility functions for Sales Return module.
 * Structured to easily switch to TanStack Query + API calls later.
 */

import { useMemo } from 'react';
import { keepPreviousData, useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getRequest, postRequest } from '../../../services/httpService';
import { useDebounce } from '../../Inventory/hooks/useDebounce';
import {
  subTableHeaderCellBaseSx,
  tableHeaderCellBaseSx,
  tableHeaderFirstCellRadiusSx,
  tableHeaderLastCellRadiusSx,
  tableHeaderSx,
  tableColumnSx,
  selectedTableCellSx,
} from '../../Inventory/Common/inventory.helper';

// ============ Types ============

export interface SalesReturnItem {
  id: number;
  item_name: string;
  item_code: string;
  item_id: number;
  batch_code: string;
  batch_id: number | null;
  return_quantity: string;
  return_uom: number;
  return_uom_name: string;
  return_uom_code: string;
  concession_amount: string;
  concession_percentage: string;
  sgst_percentage: string;
  sgst_amount: string;
  cgst_percentage: string;
  cgst_amount: string;
  igst_percentage: string;
  igst_amount: string;
  net_amount: string;
  total_amount: string;
  mrp: string;
  created_on: string;
  updated_on: string;
}

export interface SalesReturn {
  id: number;
  patient_id: number;
  return_number: string;
  return_date: string;
  status: string;
  patient_name: string;
  patient_mr_no: string;
  patient_mobile: string;
  patient_age: number;
  patient_gender: string;
  original_invoice_number: string;
  clinic_name: string;
  return_amount: string;
  net_amount: string;
  paid_amount: string;
  balance_amount: string;
  total_amount: string;
  total_concession_amount: string;
  sgst_amount: string;
  cgst_amount: string;
  round_off: string;
  remarks: string;
  created_by_name: string;
  approved_by_name: string | null;
  approved_at: string | null;
  rejected_at: string | null;
  total_items: number;
  return_items: SalesReturnItem[];
  created_on: string;
  updated_on: string;
  rejection_reason:string;
}

export type SalesReturnStatusType = 'All' | 'pending' | 'approved' | 'rejected';

export interface SalesReturnsApiResponse {
  meta: {
    limit: number;
    next: string | null;
    offset: number;
    previous: string | null;
    total_count: number;
  };
  objects: SalesReturn[];
  status_counts: {
    total_count: number;
    pending_count: number;
    approved_count: number;
    rejected_count: number;
  };
}

export interface SalesReturnsQueryParams {
  limit?: number;
  offset?: number;
  status?: string;
  search?: string;
  from_date?: string;
  to_date?: string;
  settlement_status?: string;
  clinic?: number;
  patient_id?: number;
}

export interface SalesReturnTab {
  label: string;
  value: SalesReturnStatusType;
  count: number;
}

export interface SalesReturnFilterValues {
  fromDate: string;
  toDate: string;
  settlementStatus: 'settled' | 'unsettled';
}

export interface SalesReturnActionResponse {
  message: string;
  sales_return: {
    id: number;
    return_number: string;
    status: string;
    approved_by?: string;
    approved_at?: string;
    rejected_at?: string;
  };
}

export interface CreditTransactionConsumption {
  credit_transaction_id: number;
  consume_amount: string;
}

export interface PaymentDetail {
  payment_mode_id: number;
  number?: string;
  paid_amount: string;
  bank_id?: number;
  account_name?: string;
  branch_name?: string;
  ifsc_code?: string;
  package_advance_id?: number;
  credit_transaction_consumptions?: CreditTransactionConsumption[];
}

export interface SalesReturnCreatePaymentPayload {
  settlement_option: 'REFUND_TO_PATIENT' | 'ADD_TO_ADVANCE';
  payment_details?: PaymentDetail[];
  amount: string;
  advance_type: string;
}

export interface SalesReturnCreatePaymentResponse {
  message: string;
  payment_id?: number;
  advance_id?: number;
}

export interface PaymentMode {
  id: number;
  name: string;
  code: number;
  translation: Record<string, string> | null;
}

export interface BillingConfigOptions {
  modeofpayment?: PaymentMode[];
  [key: string]: unknown;
}

// ============ Table UI (GRN-aligned) ============

export const getSalesReturnTableHeaders = (t: (key: string) => string) => [
  t('Table_Header_Return_No_Date'),
  t('Table_Header_Patient'),
  t('Table_Header_Return_Amount'),
  t('Table_Header_Paid_Amount'),
  t('Table_Header_Balance_Amount'),
  t('Table_Header_Status'),
  t('Table_Header_Remark'),
  t('Table_Header_Actions'),
] as const;

export const getSalesReturnItemsTableHeaders = (t: (key: string) => string) => [
  t('Table_Header_Item_Name_Code'),
  t('Table_Header_Batch_Code'),
  t('Table_Header_Return_Qty_UOM'),
  t('Table_Header_MRP'),
  t('Table_Header_Concession'),
  t('Table_Header_Total_Amount'),
  t('Table_Header_SGST'),
  t('Table_Header_CGST'),
  t('Table_Header_Net_Amount'),
] as const;

export const SALES_RETURN_ITEMS_TABLE_MIN_WIDTH_PX = 1100;

// Typography styles (match GRN)
export const SALES_RETURN_TABLE_HEADER_TEXT_SX = tableHeaderSx;
export const SALES_RETURN_TABLE_BODY_TEXT_SX = tableColumnSx;
export const SALES_RETURN_TABLE_SUBTEXT_SX = {
  ...tableHeaderSx,
  color: 'gray',
} as const;

export const getSalesReturnItemBodyCellSx = (isExpanded: boolean) => {
  // GRN uses selectedTableCellSx for body rows inside the Collapse area.
  return {
    ...selectedTableCellSx(isExpanded),
  };
};

export const getSalesReturnHeaderCellSx = (index: number, total: number) => {
  const isFirst = index === 0;
  const isLast = index === total - 1;

  return {
    ...tableHeaderCellBaseSx,
    ...(isFirst ? tableHeaderFirstCellRadiusSx : null),
    ...(isLast ? tableHeaderLastCellRadiusSx : null),
  };
};

export const getSalesReturnItemHeaderCellSx = (index: number, total: number) => {
  const isFirst = index === 0;
  const isLast = index === total - 1;

  return {
    ...subTableHeaderCellBaseSx,
    ...(isFirst ? tableHeaderFirstCellRadiusSx : null),
    ...(isLast
      ? {
        ...tableHeaderLastCellRadiusSx,
        textAlign: 'center',
      }
      : null),
  };
};

// Kept for backwards compatibility with earlier refactor; prefer Typography with SALES_RETURN_TABLE_BODY_TEXT_SX
export const salesReturnItemValueCellSx = selectedTableCellSx(true);

// ============ Status Tabs Configuration ============

export const getSalesReturnTabs = (t: (key: string) => string): SalesReturnTab[] => [
  { label: t('Status_All'), value: 'All', count: 0 },
  { label: t('Status_Pending'), value: 'pending', count: 0 },
  { label: t('Status_Approved'), value: 'approved', count: 0 },
  { label: t('Status_Rejected'), value: 'rejected', count: 0 },
];

// ============ Status Colors ============

export const getStatusColor = (status: string): { bgColor: string; textColor: string} => {
  switch (status) {
  case 'pending':
    return { bgColor: '#FFF3E0', textColor: '#E65100' };
  case 'approved':
    return { bgColor: '#E8F5E9', textColor: '#2E7D32' };
  case 'rejected':
    return { bgColor: '#FCE4EC', textColor: '#AD1457' };
  default:
    return { bgColor: '#F5F5F5', textColor: '#616161' };
  }
};



// ============ Mock Data ============



/**
 * Mutation hook for approving sales return
 * API: POST /sales-returns/{id}/update-status/
 */
export const useApproveSalesReturn = () => {
  const queryClient = useQueryClient();
  
  return useMutation<SalesReturnActionResponse, Error, number>({
    mutationFn: async (id: number) => {
      return postRequest(`/sales-returns/${id}/update-status/`, {
        action: 'approve',
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sales-returns-list'] });
    },
  });
};

/**
 * Mutation hook for rejecting sales return
 * API: POST /sales-returns/{id}/update-status/
 */
export const useRejectSalesReturn = () => {
  const queryClient = useQueryClient();
  
  return useMutation<SalesReturnActionResponse, Error, { id: number; rejectRemark: string }>({
    mutationFn: async ({ id, rejectRemark }: { id: number; rejectRemark: string }) => {
      return postRequest(`/sales-returns/${id}/update-status/`, {
        action: 'reject',
        rejection_reason: rejectRemark,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sales-returns-list'] });
    },
  });
};

/**
 * Mutation hook for creating sales return payment
 * API: POST /sales-returns/{id}/create-payment/
 */
export const useCreateSalesReturnPayment = () => {
  const queryClient = useQueryClient();
  
  return useMutation<
    SalesReturnCreatePaymentResponse,
    Error,
    { id: number; payload: SalesReturnCreatePaymentPayload }
  >({
    mutationFn: async ({ id, payload }) => {
      return postRequest(`/sales-returns/${id}/create-payment/`, payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sales-returns-list'] });
    },
  });
};

/**
 * Fetch billing config options for a patient
 * API: GET /billing/billing-config-options/?patient_id={patientId}
 */
async function fetchBillingConfigOptions(patientId: number): Promise<BillingConfigOptions> {
  return getRequest<BillingConfigOptions>('/billing/billing-config-options/', {
    params: { patient_id: patientId },
  });
}

/**
 * Hook to fetch billing config options for a patient
 */
export function useBillingConfigOptions(patientId: number | null) {
  return useQuery({
    queryKey: ['billing-config-options', patientId],
    queryFn: () => fetchBillingConfigOptions(patientId!),
    enabled: patientId !== null,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    gcTime: 10 * 60 * 1000, // Keep in cache for 10 minutes
    refetchOnWindowFocus: false,
    refetchOnMount: 'always', // Refetch when navigating back to screen
    refetchOnReconnect: false,
  });
}

// ============ API Integration with TanStack Query ============

/**
 * Fetch sales returns from API with filters
 */
async function fetchSalesReturns(params: SalesReturnsQueryParams): Promise<SalesReturnsApiResponse> {
  const queryParams: Record<string, string | number> = {
    limit: params.limit ?? 100,
    offset: params.offset ?? 0,
  };

  if (params.status && params.status !== 'All') {
    queryParams.status = params.status.toLowerCase();
  }

  if (params.search) {
    queryParams.search = params.search;
  }

  if (params.from_date) {
    queryParams.from_date = params.from_date;
  }

  if (params.to_date) {
    queryParams.to_date = params.to_date;
  }

  if (params.settlement_status) {
    queryParams.settlement_status = params.settlement_status;
  }

  if (params.clinic) {
    queryParams.clinic = params.clinic;
  }

  if (params.patient_id) {
    queryParams.patient_id = params.patient_id;
  }

  return getRequest<SalesReturnsApiResponse>('/sales-returns', { params: queryParams });
}

/**
 * Hook to query sales returns with search debouncing and filters
 */
export function useSalesReturnsQuery(args: {
  limit?: number;
  offset?: number;
  status?: string;
  search?: string;
  fromDate?: string;
  toDate?: string;
  settlementStatus?: string;
  clinicId?: number;
  patientId?: number;
  debounceMs?: number;
  enabled?: boolean;
}) {
  const debouncedSearch = useDebounce(args.search ?? '', args.debounceMs ?? 350);

  const params = useMemo<SalesReturnsQueryParams>(
    () => ({
      limit: args.limit,
      offset: args.offset,
      status: args.status,
      search: debouncedSearch.trim() || undefined,
      from_date: args.fromDate || undefined,
      to_date: args.toDate || undefined,
      settlement_status: args.settlementStatus || undefined,
      clinic: args.clinicId || undefined,
      patient_id: args.patientId || undefined,
    }),
    [args.limit, args.offset, args.status, debouncedSearch, args.fromDate, args.toDate, args.settlementStatus, args.clinicId, args.patientId]
  );

  return useQuery({
    queryKey: ['sales-returns-list', params],
    queryFn: () => fetchSalesReturns(params),
    enabled: args.enabled ?? true,
    staleTime: 30 * 1000, // 30 seconds
    placeholderData: keepPreviousData,
  });
}
